// src/utils/notificationBatch.js
import { createBulkNotifications } from '../services/notificationsService';

/**
 * Clase para gestionar el procesamiento por lotes de notificaciones
 * Esto ayuda a reducir la carga en la base de datos y evitar problemas
 * de rendimiento al enviar muchas notificaciones simultáneamente.
 */
class NotificationBatch {
  // Tamaño máximo de lote antes de procesar
  static MAX_BATCH_SIZE = 100;
  
  // Intervalo máximo de espera antes de procesar (en ms)
  static MAX_INTERVAL = 10000; // 10 segundos
  
  // Cola de notificaciones pendientes
  static pendingNotifications = [];
  
  // Temporizador para procesamiento periódico
  static timeoutId = null;
  
  // Si está procesando actualmente
  static isProcessing = false;
  
  /**
   * Añade notificaciones a la cola pendiente
   * @param {Array} notifications - Array de objetos de notificación
   */
  static addToBatch(notifications) {
    if (!Array.isArray(notifications)) {
      notifications = [notifications];
    }
    
    // Añadir a las notificaciones pendientes
    this.pendingNotifications.push(...notifications);
    
    // Si no hay temporizador activo, iniciar uno
    if (!this.timeoutId) {
      this.startProcessingTimer();
    }
    
    // Si se excede el tamaño máximo, procesar inmediatamente
    if (this.pendingNotifications.length >= this.MAX_BATCH_SIZE) {
      this.processBatch();
    }
  }
  
  /**
   * Inicia el temporizador para procesamiento periódico
   */
  static startProcessingTimer() {
    this.timeoutId = setTimeout(() => {
      this.processBatch();
    }, this.MAX_INTERVAL);
  }
  
  /**
   * Procesa las notificaciones pendientes
   */
  static async processBatch() {
    // Si ya está procesando o no hay notificaciones, salir
    if (this.isProcessing || this.pendingNotifications.length === 0) {
      return;
    }
    
    // Marcar como procesando
    this.isProcessing = true;
    
    // Limpiar temporizador si existe
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    
    try {
      // Tomar el lote actual y limpiar la cola
      const currentBatch = [...this.pendingNotifications];
      this.pendingNotifications = [];
      
      console.log(`Procesando lote de ${currentBatch.length} notificaciones`);
      
      // Agrupar notificaciones por destinatario y tipo para reducir duplicados
      const groupedByRecipient = currentBatch.reduce((acc, notification) => {
        const userId = notification.user_id;
        const type = notification.notification_type;
        const key = `${userId}:${type}`;
        
        if (!acc[key]) {
          acc[key] = [];
        }
        
        acc[key].push(notification);
        return acc;
      }, {});
      
      // Procesar cada grupo por separado
      for (const [key, notifications] of Object.entries(groupedByRecipient)) {
        // Si hay varias notificaciones para el mismo usuario y tipo,
        // mantener solo la más reciente para evitar spam
        if (notifications.length > 1) {
          const mostRecent = notifications.reduce((mostRecent, current) => {
            return !mostRecent || new Date(current.created_at) > new Date(mostRecent.created_at)
              ? current
              : mostRecent;
          }, null);
          
          // Usar mostRecent si existe
          if (mostRecent) {
            try {
              await createBulkNotifications([mostRecent.user_id], {
                title: mostRecent.title,
                message: mostRecent.message,
                notification_type: mostRecent.notification_type,
                related_entity_type: mostRecent.related_entity_type,
                related_entity_id: mostRecent.related_entity_id
              });
            } catch (error) {
              console.error('Error procesando notificación agrupada:', error);
            }
          }
        } else if (notifications.length === 1) {
          // Solo hay una notificación, procesarla directamente
          const notification = notifications[0];
          
          try {
            await createBulkNotifications([notification.user_id], {
              title: notification.title,
              message: notification.message,
              notification_type: notification.notification_type,
              related_entity_type: notification.related_entity_type,
              related_entity_id: notification.related_entity_id
            });
          } catch (error) {
            console.error('Error procesando notificación única:', error);
          }
        }
      }
      
      console.log('Procesamiento de lote completado');
    } catch (error) {
      console.error('Error general procesando lote de notificaciones:', error);
    } finally {
      // Desmarcar como procesando
      this.isProcessing = false;
      
      // Si hay nuevas notificaciones pendientes, reiniciar el temporizador
      if (this.pendingNotifications.length > 0) {
        this.startProcessingTimer();
      }
    }
  }
}

export default NotificationBatch;